import { AlertCircle, AlertTriangle, Gavel, Loader } from 'lucide-react';

import { useJudgeVerdict } from '../../hooks/useJudgeVerdict';
import { type JudgeEvidence } from '../../services/api/judge';
import { getApiErrorDetails } from '../../utils/api-error';
import { cn } from '../../utils/cn';
import { formatDateTime } from '../../utils/datetime';

import { intentLabel, outcomeBadgeColor, outcomeLabel, rootCauseLabel } from './kvalitet-labels';
import { scrollToTurn } from './scroll-to-turn';
import { sessionVerdictView, type ReadableVerdictRow } from './session-verdict-view';

const TurnQuote = ({ item, tone }: { item: JudgeEvidence; tone: 'red' | 'slate' }) => (
  <li
    className={cn(
      'flex items-start gap-2 rounded-lg border p-2 text-xs leading-5',
      tone === 'red'
        ? 'border-red-200 bg-red-50 text-red-800'
        : 'border-slate-200 bg-white/70 text-slate-700',
    )}
  >
    <button
      type="button"
      onClick={() => scrollToTurn({ index: item.turn })}
      className="flex-shrink-0 rounded bg-indigo-100 px-1.5 py-0 text-[10px] font-medium text-indigo-700 hover:bg-indigo-200"
    >
      Tur {item.turn}
    </button>
    <span className="italic">”{item.quote}”</span>
  </li>
);

const Pill = ({ label, value }: { label: string; value: boolean }) => (
  <span
    className={cn(
      'rounded-full border px-2 py-0.5 text-[11px] font-medium',
      value
        ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
        : 'border-slate-200 bg-slate-100 text-slate-600',
    )}
  >
    {label}: {value ? 'ja' : 'nej'}
  </span>
);

const SectionLabel = ({ children }: { children: string }) => (
  <div className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-slate-500">
    {children}
  </div>
);

const VerdictBody = ({ row }: { row: ReadableVerdictRow }) => {
  const verdict = row.verdict;
  return (
    <div className="mt-3 space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <span
          className={cn(
            'rounded-full border px-2.5 py-0.5 text-xs font-semibold',
            outcomeBadgeColor(verdict.outcome),
          )}
        >
          {outcomeLabel(verdict.outcome)}
        </span>
        {verdict.rootCause && (
          <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[11px] text-slate-700">
            {rootCauseLabel(verdict.rootCause)}
          </span>
        )}
        <span className="text-[11px] text-slate-500">{intentLabel(verdict.intent)}</span>
      </div>

      <p className="text-sm leading-6 text-slate-700">
        <span className="font-medium text-slate-900">Mål:</span> {verdict.customerGoal}
      </p>
      {verdict.missingForBot && (
        <p className="text-sm leading-6 text-slate-700">
          <span className="font-medium text-slate-900">Saknades:</span> {verdict.missingForBot}
        </p>
      )}

      <div className="flex flex-wrap gap-2">
        <Pill label="Kunde boten löst det" value={verdict.couldBotHaveSolved} />
        <Pill label="Gav nästa steg" value={verdict.gaveNextStep} />
      </div>

      {verdict.factualErrors.length > 0 && (
        <div>
          <SectionLabel>Faktafel</SectionLabel>
          <ul className="space-y-1.5">
            {verdict.factualErrors.map((item, i) => (
              <TurnQuote key={i} item={item} tone="red" />
            ))}
          </ul>
        </div>
      )}

      {verdict.evidence.length > 0 && (
        <div>
          <SectionLabel>Bevis</SectionLabel>
          <ul className="space-y-1.5">
            {verdict.evidence.map((item, i) => (
              <TurnQuote key={i} item={item} tone="slate" />
            ))}
          </ul>
        </div>
      )}

      <details className="rounded-lg border border-slate-200 bg-white/70 p-2 text-xs text-slate-700">
        <summary className="cursor-pointer font-medium text-slate-600">Domarens resonemang</summary>
        <p className="mt-2 whitespace-pre-wrap leading-5">{verdict.reasoning}</p>
      </details>

      <div className="text-[11px] text-slate-500">
        {row.judgeModel ?? 'okänd modell'} &bull; {formatDateTime(row.judgedAt)} &bull;{' '}
        {row.promptVersion}
      </div>
    </div>
  );
};

export const SessionVerdictPanel = ({ sessionId }: { sessionId: string }) => {
  const query = useJudgeVerdict(sessionId);
  const data = query.data;
  const view = data ? sessionVerdictView(data) : null;

  return (
    <div className="rounded-xl border border-slate-200 bg-gradient-to-br from-slate-50 to-white p-4">
      <div className="flex items-center gap-2">
        <Gavel className="h-4 w-4 text-slate-600" />
        <h4 className="text-sm font-semibold text-slate-900">Sessionsdomaren</h4>
      </div>

      {query.isLoading ? (
        <div className="mt-3 flex items-center gap-2 text-xs text-slate-500">
          <Loader className="h-3 w-3 animate-spin" />
          Hämtar domen…
        </div>
      ) : query.error ? (
        <div className="mt-3 flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
          <AlertCircle className="h-3 w-3" />
          Kunde inte hämta domen: {getApiErrorDetails(query.error)}
        </div>
      ) : !data || !view ? (
        <p className="mt-3 text-xs text-slate-500">Inte bedömd ännu — domaren kör nattetid.</p>
      ) : (
        <>
          {view.failure && (
            <div className="mt-3 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
              <AlertTriangle className="mt-0.5 h-3 w-3 flex-shrink-0" />
              <span>
                Domaren kunde inte bedöma sessionen: {view.failure.errorCode ?? 'okänt fel'} efter{' '}
                {view.failure.attempts} försök
                {view.failure.kind === 'permanent' ? ', permanent fel' : ''}.
                {view.shown ? ' Nedan visas en äldre dom.' : ''}
              </span>
            </div>
          )}
          {view.unreadablePromptVersion && (
            <div className="mt-3 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
              <AlertTriangle className="mt-0.5 h-3 w-3 flex-shrink-0" />
              <span>
                Domen kunde inte läsas (promptversion {view.unreadablePromptVersion}).
                {view.shown ? ' Nedan visas en annan dom.' : ''}
              </span>
            </div>
          )}
          {view.shown && view.shown.promptVersion !== data.promptVersion && (
            <p className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
              Domen gäller promptversion {view.shown.promptVersion}, inte nattens{' '}
              {data.promptVersion}.
            </p>
          )}
          {view.shown && <VerdictBody row={view.shown} />}
          {view.notJudgedYet && (
            <p className="mt-3 text-xs text-slate-500">
              Inte bedömd med promptversion {data.promptVersion} ännu.
            </p>
          )}
        </>
      )}
    </div>
  );
};
